import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useLocationStore } from '@/store/location';
import { useToast } from '@/components/common/Toast';
import { useAuth } from '@/contexts/AuthContext';
import { restaurantService, type Restaurant } from '@/services/restaurant';
import { productService, type ProductOffer } from '@/services/product';
import { favoritesService } from '@/services/favorites';
import { calculateDistance } from '@/lib/distance';
import { useReviewStats } from '@/hooks/useReviewStats';
import { DesktopLayout } from '@/components/desktop/DesktopLayout';
import { StoreImageCarousel } from '@/components/store/StoreImageCarousel';
import { CartButton } from '@/components/common/CartButton';
import { PickupTimeNotice } from '@/components/store/PickupTimeNotice';
import { useRealtimeRestaurantStock } from '@/hooks/useRealtimeStock';
import { SEO } from '@/components/seo/SEO';
import { generateStoreSEO } from '@/utils/seo';
import type { RestaurantWithProduct } from '@/types';
import type { Product as StoreProduct, OrderType } from '@/pages/store/store-detail.types';

// Desktop-specific components
import { DesktopStoreInfoCard } from '@/components/desktop/store/DesktopStoreInfoCard';
import { DesktopOrderTypeTabs } from '@/components/desktop/store/DesktopOrderTypeTabs';
import { DesktopProductGrid } from '@/components/desktop/store/DesktopProductGrid';
import { getKoreanDate } from '@/lib/dateUtils';

type Product = StoreProduct;

interface DesktopStoreDetailPageProps {
  storeId?: string;
  readonly?: boolean;
}

type DeadlineOfferCandidate = {
  offer_type?: ProductOffer['offer_type'];
  is_active?: ProductOffer['is_active'];
  valid_from?: string | null;
  valid_until?: string | null;
};

const isDeadlineDiscountOfferActiveToday = (
  offer: DeadlineOfferCandidate,
  today: string,
  now: Date
) => {
  if (offer.offer_type !== 'DEADLINE_DISCOUNT' || offer.is_active !== true) {
    return false;
  }

  if (offer.valid_from) {
    const offerDate = offer.valid_from.includes('T')
      ? offer.valid_from.split('T')[0]
      : offer.valid_from;
    if (offerDate !== today) {
      return false;
    }
  }

  if (offer.valid_until) {
    const validUntil = new Date(offer.valid_until);
    if (!Number.isNaN(validUntil.getTime()) && now > validUntil) {
      return false;
    }
  }

  return true;
};

const hasActiveDeadlineOfferToday = (
  offers: Array<DeadlineOfferCandidate | undefined> | undefined,
  today: string,
  now: Date
) => {
  if (!offers || offers.length === 0) {
    return false;
  }

  return offers.some(
    (offer) => offer && isDeadlineDiscountOfferActiveToday(offer, today, now)
  );
};

export function DesktopStoreDetailPage({ storeId: propStoreId, readonly = false }: DesktopStoreDetailPageProps = {}) {
  const { storeId: paramStoreId } = useParams<{ storeId: string }>();
  const actualStoreId = propStoreId || paramStoreId;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentLocation } = useLocationStore();
  const { showToast, ToastContainer } = useToast();
  const { user } = useAuth();
  
  // URL 파라미터에서 주문유형 읽기
  const urlOrderType = searchParams.get('orderType') as OrderType | null;
  const validOrderTypes: OrderType[] = ['DEADLINE_DISCOUNT', 'RESERVATION_DISCOUNT', 'REGULAR_ORDER'];
  const initialOrderType = urlOrderType && validOrderTypes.includes(urlOrderType) ? urlOrderType : 'DEADLINE_DISCOUNT';
  
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrderType, setSelectedOrderType] = useState<OrderType>(initialOrderType);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isCheckingFavorite, setIsCheckingFavorite] = useState(false);
  const [hidingProducts, setHidingProducts] = useState<Set<string>>(new Set());
  const isFetchingDataRef = useRef(false);
  const isCheckingFavoriteRef = useRef(false);

  // 리뷰 통계 계산
  const { calculatedRating, calculatedTotalReviews, isLoadingReviewStats } = useReviewStats(actualStoreId || null);

  // Reuse mobile data loading logic
  useEffect(() => {
    const fetchStoreData = async () => {
      if (!actualStoreId || isFetchingDataRef.current) return;
      
      try {
        isFetchingDataRef.current = true;
        setIsLoading(true);
        
        // 매장 정보 조회
        const restaurantResult = await restaurantService.getRestaurantById(actualStoreId);
        if (restaurantResult.success && restaurantResult.data) {
          setRestaurant(restaurantResult.data);
        }
        
        // 상품 목록 조회
        const productsList = await productService.getProductsByRestaurant(actualStoreId);
        if (productsList && productsList.length > 0) {
          const todayKST = getKoreanDate();
          const now = new Date();
          const localProducts: Product[] = productsList.map(product => {
            const currentOrderType = selectedOrderType;
            const currentOffer = product.offers?.find(offer => offer.offer_type === currentOrderType) || product.offers?.[0];
            const deadlineOfferAvailableToday =
              currentOrderType === 'DEADLINE_DISCOUNT'
                ? hasActiveDeadlineOfferToday(product.offers, todayKST, now)
                : true;
            
            return {
              id: product.id,
              name: product.name,
              description: product.description,
              category: product.category,
              original_price: product.original_price,
              discounted_price: currentOffer?.discounted_price || product.original_price,
              discount_rate: currentOffer?.discount_rate || 0,
              discount_type: currentOffer?.offer_type || 'REGULAR_ORDER',
              stock_quantity: product.stock?.[0]?.current_stock || 0,
              max_order_quantity: currentOffer?.max_quantity_per_order || 10,
              is_available: product.is_available,
              image_url: product.image_url,
              ingredients: product.ingredients,
              allergens: product.allergens,
              calories: product.calories,
              allOffers: product.offers || [],
              deadlineOfferAvailableToday
            };
          });
          setProducts(localProducts);
        }
      } catch (error) {
        console.error('Failed to fetch store data:', error);
      } finally {
        setIsLoading(false);
        isFetchingDataRef.current = false;
      }
    };

    fetchStoreData();
  }, [actualStoreId]);

  // 거리 계산
  useEffect(() => {
    if (restaurant && currentLocation?.coordinate && 
        currentLocation.coordinate.latitude !== 0 && 
        currentLocation.coordinate.longitude !== 0 &&
        restaurant.latitude && restaurant.longitude) {
      calculateDistance(
        currentLocation.coordinate.latitude,
        currentLocation.coordinate.longitude,
        restaurant.latitude,
        restaurant.longitude
      );
    }
  }, [restaurant?.id, currentLocation?.coordinate?.latitude, currentLocation?.coordinate?.longitude]);

  // 즐겨찾기 상태 확인
  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (!actualStoreId || isCheckingFavoriteRef.current) return;
      
      try {
        isCheckingFavoriteRef.current = true;
        setIsCheckingFavorite(true);
        const result = await favoritesService.isFavorite(user?.id || null, actualStoreId);
        if (result.success) {
          setIsFavorite(result.data || false);
        }
      } catch (error) {
        console.error('Failed to check favorite status:', error);
      } finally {
        setIsCheckingFavorite(false);
        isCheckingFavoriteRef.current = false;
      }
    };

    checkFavoriteStatus();
  }, [user?.id, actualStoreId]);

  // 실시간 재고 업데이트 구독
  useRealtimeRestaurantStock(actualStoreId || null, {
    onStockUpdate: (productId, newStock, isAvailable) => {
      setProducts(prev => prev.map(product => 
        product.id === productId 
          ? { ...product, stock_quantity: newStock, is_available: Boolean(isAvailable) }
          : product
      ));
    },
    onOutOfStock: (productId, productName) => {
      setHidingProducts(prev => new Set(prev).add(productId));
      showToast(`${productName}이(가) 품절되었습니다`);
      
      setTimeout(() => {
        setProducts(prev => prev.filter(p => p.id !== productId));
        setHidingProducts(prev => {
          const newSet = new Set(prev);
          newSet.delete(productId);
          return newSet;
        });
      }, 1000);
    },
    onBackInStock: (productId, productName) => {
      setHidingProducts(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
      showToast(`${productName}이(가) 재입고되었습니다`);
    }
  });

  // Update product pricing when order type changes
  useEffect(() => {
    const todayKST = getKoreanDate();
    const now = new Date();
    setProducts(prevProducts => 
      prevProducts.map(product => {
        const selectedOffer = product.allOffers?.find(offer => offer.offer_type === selectedOrderType);
        const deadlineOfferAvailableToday =
          selectedOrderType === 'DEADLINE_DISCOUNT'
            ? hasActiveDeadlineOfferToday(product.allOffers, todayKST, now)
            : true;
        
        if (selectedOffer) {
          return {
            ...product,
            discounted_price: selectedOffer.discounted_price,
            discount_rate: selectedOffer.discount_rate,
            discount_type: selectedOffer.offer_type,
            max_order_quantity: selectedOffer.max_quantity_per_order,
            deadlineOfferAvailableToday
          };
        } else {
          return {
            ...product,
            discounted_price: product.original_price,
            discount_rate: 0,
            discount_type: selectedOrderType,
            max_order_quantity: 10,
            deadlineOfferAvailableToday
          };
        }
      })
    );
  }, [selectedOrderType]);

  // Filter products by selected order type
  const filteredProducts = products.filter(product => {
    const hasOfferForType = product.allOffers?.some(offer => offer.offer_type === selectedOrderType);
    return hasOfferForType || !product.allOffers || product.allOffers.length === 0;
  });

  // 즐겨찾기 토글
  const handleFavoriteToggle = async () => {
    if (!actualStoreId || isCheckingFavorite || readonly) return;
    
    if (!user) {
      showToast('로그인이 필요합니다');
      navigate('/login');
      return;
    }
    
    try {
      setIsCheckingFavorite(true);
      const result = await favoritesService.toggleFavorite(user.id, actualStoreId);
      
      if (result.success) {
        const newIsFavorite = !isFavorite;
        setIsFavorite(newIsFavorite);
        
        if (newIsFavorite) {
          showToast('즐겨찾기에 추가되었습니다');
          setTimeout(() => {
            if (window.confirm('단골가게 페이지에서 확인하시겠습니까?')) {
              navigate('/favorites');
            }
          }, 1000);
        } else {
          showToast('즐겨찾기에서 제거되었습니다');
        }
      } else {
        showToast(result.error || '즐겨찾기 처리에 실패했습니다');
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      showToast('즐겨찾기 처리에 실패했습니다');
    } finally {
      setIsCheckingFavorite(false);
    }
  };

  // 상품 클릭 핸들러
  const handleOrderTypeChange = (newOrderType: OrderType) => {
    setSelectedOrderType(newOrderType);
    // URL 업데이트 (replace로 히스토리 교체)
    navigate(`/store/${actualStoreId}?orderType=${newOrderType}`, { replace: true });
  };

  const handleProductClick = (productId: string) => {
    if (readonly) return;
    navigate(`/store/${actualStoreId}/product/${productId}?orderType=${selectedOrderType}`);
  };

  // 리뷰 페이지로 이동
  const handleReviewClick = () => {
    if (readonly) return;
    navigate(`/store/${actualStoreId}/reviews`);
  };

  // 매장 정보 페이지로 이동
  const handleStoreInfoClick = () => {
    navigate(`/store/${actualStoreId}/info`);
  };

  if (isLoading) {
    return (
      <DesktopLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-500 mx-auto mb-6"></div>
            <p className="text-[18px] text-gray-600">매장 정보를 불러오는 중...</p>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  if (!restaurant) {
    return (
      <DesktopLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-[18px] text-gray-600 mb-6">매장 정보를 찾을 수 없습니다</p>
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 text-[16px] font-medium"
            >
              돌아가기
            </button>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  // 동적 SEO 생성
  const seoProps = restaurant ? generateStoreSEO({
    ...restaurant,
    pickup_instructions: restaurant.pickup_instructions || '픽업 정보 없음'
  } as RestaurantWithProduct) : null;

  return (
    <>
      {seoProps && <SEO {...seoProps} />}
      <DesktopLayout>
        <div className="bg-white">
          {/* Hero Section - Top: Image, Bottom: Store Info */}
          <div className="mb-8">
          {/* Store Image Carousel */}
          <div className="w-full mb-2">
            <StoreImageCarousel 
              images={restaurant.image_url && restaurant.image_url.trim() ? [restaurant.image_url] : []}
              storeName={restaurant.name}
            />
          </div>
          
          {/* Store Information */}
          <div className="max-w-4xl mx-auto">
            <DesktopStoreInfoCard 
              restaurant={restaurant}
              isFavorite={isFavorite}
              isCheckingFavorite={isCheckingFavorite}
              readonly={readonly}
              calculatedRating={calculatedRating}
              calculatedTotalReviews={calculatedTotalReviews}
              isLoadingReviewStats={isLoadingReviewStats}
              onFavoriteToggle={handleFavoriteToggle}
              onReviewClick={handleReviewClick}
              onStoreInfoClick={handleStoreInfoClick}
            />
          </div>
        </div>

        {/* Order Type Tabs */}
        <DesktopOrderTypeTabs
          selectedOrderType={selectedOrderType}
          onOrderTypeChange={handleOrderTypeChange}
        />

        {/* Pickup time notice for deadline discount */}
        <div className="max-w-5xl mx-auto w-full px-6">
          <PickupTimeNotice
            restaurant={restaurant}
            products={filteredProducts}
            selectedOrderType={selectedOrderType}
          />
        </div>

        {/* Products Grid */}
        <DesktopProductGrid
          products={filteredProducts}
          hidingProducts={hidingProducts}
          readonly={readonly}
          onProductClick={handleProductClick}
        />

        {/* Floating Cart Button */}
        {!readonly && (
          <div className="fixed bottom-8 right-8 z-50">
            <CartButton 
              variant="default" 
              size="large" 
              className="bg-white shadow-lg border border-gray-200 rounded-full hover:shadow-xl"
            />
          </div>
        )}
        
        <ToastContainer />
      </div>
    </DesktopLayout>
    </>
  );
}
